import React from "react";
import { format } from "date-fns";
import { CalendarDays, Loader2, Percent, X } from "lucide-react";

import { useBookingFlow } from "../booking/useBookingFlow";
import { PollingScreen, SuccessScreen, FailedScreen } from "../booking/PaymentScreens";
import { DateSlotPicker } from "../booking/DateSlotPicker";
import {
  ContactFields,
  PackageSelect,
  LocationField,
  AddonPicker,
  NotesField,
  ReferredByField,
  InfoBoxes,
} from "../booking/BookingFormFields";

export default function BookingSection() {
  const flow = useBookingFlow();
  const {
    services,
    addons,
    deposit,
    date,
    setDate,
    availability,
    loadingSlots,
    submitting,
    paymentState,
    setPaymentState,
    confirmedBooking,
    form,
    setForm,
    update,
    selectedPackage,
    canSubmit,
    submit,
    reset,
  } = flow;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (paymentState === "polling") return <PollingScreen />;
  if (paymentState === "paid" && confirmedBooking) {
    return <SuccessScreen booking={confirmedBooking} onReset={reset} />;
  }
  if (paymentState === "failed") {
    return <FailedScreen onRetry={() => setPaymentState("idle")} />;
  }

  return (
    <section id="book" data-testid="booking-section" className="relative px-6 md:px-12 lg:px-20 py-28 md:py-40 bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-6">
            <div className="overline mb-5">— Book now</div>
            <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
              Pick a date.
              <br />
              <span className="text-white/55">We&apos;ll handle the rest.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-white/65 leading-relaxed text-sm">
              Choose a day and an available slot. Pay a fully-refundable £{deposit.amount} deposit to
              lock it in — we&apos;ll confirm by phone or email shortly after.
            </p>
          </div>
        </div>

        {paymentState === "cancelled" && (
          <div data-testid="payment-cancelled-banner" className="mb-10 border border-amber-300/30 bg-amber-300/5 text-amber-100 px-5 py-4 text-sm flex items-start gap-3">
            <X size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Payment cancelled.</div>
              <div className="text-white/65 mt-0.5">No charge was made and your slot has been released. You can book again whenever you like.</div>
            </div>
          </div>
        )}

        <div data-testid="first-wash-banner" className="mb-6 border border-emerald-300/30 bg-emerald-300/5 px-5 py-4 text-sm flex items-start gap-3">
          <Percent size={16} className="mt-0.5 flex-shrink-0 text-emerald-300" />
          <div>
            <div className="font-medium text-emerald-200">First time with STAC? 50% off your wash.</div>
            <div className="text-white/55 mt-0.5">Applied automatically for new customers — you&apos;ll pay half the package price on the day. Add-ons full price.</div>
            <div className="text-white/55 mt-1.5" data-testid="refer-a-friend-note">
              <span className="text-emerald-200/90">Refer a friend</span> — when they book and name you, you get £10 off your next wash. Automatic, no codes.
            </div>
          </div>
        </div>

        <div data-testid="weekend-only-banner" className="mb-10 border border-white/15 bg-white/[0.03] px-5 py-4 text-sm flex items-start gap-3">
          <CalendarDays size={16} className="mt-0.5 flex-shrink-0 text-white/70" />
          <div>
            <div className="font-medium text-white">Now booking 7 days a week</div>
            <div className="text-white/55 mt-0.5">Weekday and weekend slots are open — pick whichever day suits you best.</div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="booking-form"
          className="grid lg:grid-cols-12 gap-px bg-white/10 border border-white/10"
        >
          <DateSlotPicker
            date={date}
            setDate={setDate}
            availability={availability}
            loadingSlots={loadingSlots}
            form={form}
            setForm={setForm}
            today={today}
          />

          <div className="lg:col-span-7 bg-[#0E0E0E] p-6 md:p-10 space-y-6">
            <div className="overline">— Your details</div>

            <ContactFields form={form} update={update} />
            <PackageSelect form={form} setForm={setForm} services={services} selectedPackage={selectedPackage} />

            <LocationField form={form} update={update} />

            <AddonPicker addons={addons} form={form} setForm={setForm} />
            <NotesField form={form} update={update} />
            <ReferredByField form={form} update={update} />
            <InfoBoxes deposit={deposit} />

            <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-white/10">
              <div className="text-xs text-white/55">
                {date && form.time
                  ? `Booking for ${format(date, "EEE d MMM")} at ${form.time}`
                  : "Select a date and slot to continue"}
              </div>
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="booking-submit"
                className="inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 text-xs font-medium tracking-[0.22em] uppercase hover:bg-white/85 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                {submitting ? "Redirecting to Stripe…" : `Pay £${deposit.amount} & confirm`}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .bookingInput {
          background: #080808 !important;
          border: 1px solid rgba(255,255,255,0.12) !important;
          color: #fff !important;
          border-radius: 2px !important;
        }
        .bookingInput:focus, .bookingInput:focus-visible {
          border-color: rgba(255,255,255,0.55) !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .bookingInput::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </section>
  );
}
